import { useCallback, useState, useEffect } from "react";

import { Box, Flex, Image, useDisclosure } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { TaskCheckHeader } from "flows/Inventory/shared/components/TaskCheckHeader";
import { useCheckQuantityValidation } from "flows/Inventory/shared/hooks/useCheckQuantityValidation";
import { Page } from "shared/components/Page";
import { transformChunks } from "shared/components/TextWrapper";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { Button } from "ui/Button/Button";
import { IncreaseDecrease } from "ui/IncreaseDecrease/IncreaseDecrease";
import { BodyS } from "ui/Typography/Typography";
import { formatImage } from "utils/image";
import { isNotNullNorUndefined, isNullOrUndefined } from "utils/tsHelpers";

import StockChangeConfirmationModal from "../../../shared/components/StockChangeConfirmationModal";
import { useStockCheckService } from "../hooks/useStockCheckService";

const IMAGE_SIZE = 218;

export function StockCheckQuantityPage() {
  const intl = useIntl();
  const { sendSegmentTrackEvent } = useAnalytics();
  const [countedItems, setCountedItems] = useState<number>(0);

  const {
    checkId,
    origin,
    shelfLetter,
    shelfNumber,
    declareQuantity,
    sku,
    productImageUrl,
    productName,
    productCountryOfOriginName,
    loading,
    isInvalidParams,
  } = useStockCheckService();

  // Track when stock check is started
  useEffect(() => {
    if (!loading && !isInvalidParams && !isNullOrUndefined(sku)) {
      sendSegmentTrackEvent("stockCheckStarted", {
        type: "automated",
        check_id: checkId,
        product_sku: sku,
        origin,
      });
    }
  }, [loading, isInvalidParams, sku, checkId, origin, sendSegmentTrackEvent]);

  const validation = useCheckQuantityValidation({
    countedItems,
    sku,
  });

  const { isOpen: isModalOpen, onOpen: openModal, onClose: closeModal } = useDisclosure();

  const onCompleteCount = useCallback(() => {
    if (validation.handleQuantityLimitExceeded()) {
      setCountedItems(0);
      return;
    }
    if (validation.shouldShowConfirmation()) {
      openModal();
      return;
    }
    declareQuantity(countedItems);
  }, [countedItems, validation, openModal, declareQuantity]);

  const onConfirmThreshold = useCallback(() => {
    closeModal();
    declareQuantity(countedItems);
  }, [closeModal, declareQuantity, countedItems]);

  const imageUrlFormatted = isNotNullNorUndefined(productImageUrl)
    ? formatImage(productImageUrl, IMAGE_SIZE, IMAGE_SIZE)
    : null;

  if (loading || isInvalidParams) {
    return null;
  }

  return (
    <Page isCentered isBgGrey isFull isLoading={validation.loading}>
      <TaskCheckHeader
        headerMessageId="pages.stock-check-header.stock-check"
        origin={origin}
        shelfLetter={shelfLetter}
        shelfNumber={shelfNumber}
      />
      <Flex flex={1} flexDir="column" overflowY="scroll" w="100%">
        <Flex flex={1} align="center" flexDir="column" justify="center" px="s100">
          <Flex flex={1} align="center">
            {isNotNullNorUndefined(imageUrlFormatted) && (
              <Image
                src={imageUrlFormatted}
                objectFit="contain"
                h="60vh"
                maxW={`${IMAGE_SIZE}px`}
                maxH={`${IMAGE_SIZE}px`}
              />
            )}
          </Flex>
          <Box
            py="s100"
            px="s200"
            mb="s100"
            bg="white"
            borderRadius="sm"
            justifyContent="center"
            w="100%"
          >
            <BodyS
              w="100%"
              textAlign="center"
              color="grey.800"
              data-testid="stock-check-product-name"
            >
              {productName} {productCountryOfOriginName ? ` (${productCountryOfOriginName})` : null}
            </BodyS>
          </Box>
        </Flex>
        <Box bg="white" p="s200" gap="s50">
          <Box mx="s100">
            <IncreaseDecrease
              label={
                <FormattedMessage
                  id="components.increasedecrease.how_many_units"
                  values={transformChunks({ b: { as: "strong" } })}
                />
              }
              changeHandler={setCountedItems}
              count={countedItems}
              withInput
            />
          </Box>
          <Button
            size="lg"
            mt="s300"
            isFullWidth
            onClick={onCompleteCount}
            data-testid="submit-quantity-button"
          >
            {intl.formatMessage({ id: "pages.task.taskIndex.confirm_amount" })}
          </Button>
        </Box>
      </Flex>
      <StockChangeConfirmationModal
        isOpen={isModalOpen}
        currentValue={countedItems}
        onClickYes={onConfirmThreshold}
        onClickNo={closeModal}
        isStockCheck
      />
    </Page>
  );
}
